/**
 * Settings IA (redesign 1a): top-level groups with optional drill-in panels.
 * Legacy `?tab=` values from deep links are aliased onto the new groups.
 */

export type SettingsSection =
  | 'general'
  | 'notifications'
  | 'branches'
  | 'connectivity'
  | 'api-keys'
  | 'advanced'

export type GeneralPanel = 'hub' | 'mobile-app'
export type ConnectivityPanel =
  | 'hub'
  | 'integrations'
  | 'gateways'
  | 'gateway-link'
  | 'connection-health'
  | 'observers'
export type AdvancedPanel =
  | 'hub'
  | 'flagged'
  | 'review'
  | 'low-confidence'
  | 'condition-vocabulary'
  | 'databases'
  /** @deprecated Deep-link alias — opens Databases with Postgres tool selected. */
  | 'postgres-query'
  /** @deprecated Deep-link alias — opens Databases with Cypher tool selected. */
  | 'cypher-write'

export type SettingsNav = {
  section: SettingsSection
  generalPanel: GeneralPanel
  connectivityPanel: ConnectivityPanel
  advancedPanel: AdvancedPanel
}

const LEGACY_TO_SECTION: Record<string, SettingsSection> = {
  general: 'general',
  'mobile-app': 'general',
  notifications: 'notifications',
  branches: 'branches',
  connectivity: 'connectivity',
  'api-keys': 'api-keys',
  advanced: 'advanced',
  integrations: 'connectivity',
  gateways: 'connectivity',
  'gateway-link': 'connectivity',
  'connection-health': 'connectivity',
  observers: 'connectivity',
  'graph-properties': 'advanced',
  review: 'advanced',
  'low-confidence': 'advanced',
  'condition-vocabulary': 'advanced',
  'tag-acquisition': 'advanced',
  databases: 'advanced',
  'postgres-query': 'advanced',
  'cypher-write': 'advanced'
}

function isDatabasesPanel(panel: AdvancedPanel): boolean {
  return panel === 'databases' || panel === 'postgres-query' || panel === 'cypher-write'
}

export function parseSettingsNav(
  rawTab: string | null,
  debugMode: boolean,
  coreView = false,
  showApiKeys = false,
  showMobileApp = false
): SettingsNav {
  let section = LEGACY_TO_SECTION[rawTab ?? ''] ?? 'general'
  // Core hides the Branches section; a stale `?tab=branches` deep link lands
  // on General instead.
  if (coreView && section === 'branches') section = 'general'
  if (!showApiKeys && section === 'api-keys') section = 'general'
  // The Mobile app connection card is an entitlement (`ui.settings.mobile_app`);
  // its deep link lands on the General hub while the card is hidden.
  const generalPanel: GeneralPanel =
    section === 'general' && rawTab === 'mobile-app' && showMobileApp ? 'mobile-app' : 'hub'
  let connectivityPanel: ConnectivityPanel = 'hub'
  let advancedPanel: AdvancedPanel = 'hub'

  if (rawTab === 'integrations') connectivityPanel = 'integrations'
  else if (rawTab === 'gateways') connectivityPanel = 'gateways'
  else if (rawTab === 'gateway-link') connectivityPanel = 'gateway-link'
  else if (rawTab === 'connection-health') connectivityPanel = 'connection-health'
  else if (rawTab === 'observers') connectivityPanel = 'observers'

  if (rawTab === 'graph-properties') advancedPanel = 'flagged'
  else if (rawTab === 'review') advancedPanel = 'review'
  else if (rawTab === 'low-confidence') advancedPanel = 'low-confidence'
  else if (rawTab === 'condition-vocabulary') advancedPanel = 'condition-vocabulary'
  else if (rawTab === 'databases' && debugMode) advancedPanel = 'databases'
  else if (rawTab === 'postgres-query' && debugMode) advancedPanel = 'postgres-query'
  else if (rawTab === 'cypher-write' && debugMode) advancedPanel = 'cypher-write'

  if (section === 'advanced' && isDatabasesPanel(advancedPanel) && !debugMode) {
    advancedPanel = 'hub'
  }

  return { section, generalPanel, connectivityPanel, advancedPanel }
}

/** Canonical `tab` query value written for a nav state. */
export function tabParamForNav(
  nav: SettingsNav,
  debugMode: boolean,
  showApiKeys = false,
  showMobileApp = false
): string | null {
  if (nav.section === 'general') {
    return nav.generalPanel === 'mobile-app' && showMobileApp ? 'mobile-app' : null
  }
  if (nav.section === 'notifications') return 'notifications'
  if (nav.section === 'branches') return 'branches'
  if (nav.section === 'connectivity') {
    if (nav.connectivityPanel === 'integrations') return 'integrations'
    if (nav.connectivityPanel === 'gateways') return 'gateways'
    if (nav.connectivityPanel === 'gateway-link') return 'gateway-link'
    if (nav.connectivityPanel === 'connection-health') return 'connection-health'
    if (nav.connectivityPanel === 'observers') return 'observers'
    return 'connectivity'
  }
  if (nav.section === 'api-keys') return showApiKeys ? 'api-keys' : null
  if (nav.advancedPanel === 'flagged') return 'graph-properties'
  if (nav.advancedPanel === 'review') return 'review'
  if (nav.advancedPanel === 'low-confidence') return 'low-confidence'
  if (nav.advancedPanel === 'condition-vocabulary') return 'condition-vocabulary'
  if (nav.advancedPanel === 'databases' && debugMode) return 'databases'
  // Preserve legacy deep-link tab values so bookmarks keep the right tool selected.
  if (nav.advancedPanel === 'postgres-query' && debugMode) return 'postgres-query'
  if (nav.advancedPanel === 'cypher-write' && debugMode) return 'cypher-write'
  return 'advanced'
}
